/* @pepi/ui — esperarFuente
   La app espera a que Atkinson Hyperlegible Next esté cargada ANTES de montarse,
   para que el primer pintado ya use la fuente y no haya un salto de anchos (la
   agenda mide horas y columnas: un texto que cambia de ancho al llegar la fuente
   descoloca la rejilla). Pero nunca espera más de `topeMs` y nunca rechaza: si la
   red cuelga o el navegador no tiene `document.fonts`, se monta con la fuente de
   reserva (ajustada a su métrica en `tokens/fonts.css`).

   Web:   const fuente = esperarFuente()        // justo después de initTheme()
          void fuente.then(() => app.mount('#app'))   // sin top-level await (es2020)
   Móvil: const fuente = esperarFuente() ; ... ; await fuente ; app.mount('#app') */

/** Cómo terminó la espera. Ninguno es un fallo de la app: solo informa. */
export type EstadoFuente =
  /** La cara está cargada. */
  | 'cargada'
  /** Pasó `topeMs` sin respuesta: se sigue con la reserva y la fuente entrará después (`font-display: swap`). */
  | 'tope'
  /** El navegador no tiene `document.fonts` (o no tiene `load`). */
  | 'no-soportado'
  /** La carga falló, o no hay ninguna cara declarada para esa familia. */
  | 'error'

export interface OpcionesEsperarFuente {
  /** Familia CSS a esperar (por defecto Atkinson Hyperlegible Next Variable). */
  familia?: string
  /** Máximo a esperar, en ms (por defecto 1000). */
  topeMs?: number
}

export const FAMILIA_ATKINSON = 'Atkinson Hyperlegible Next Variable'

/**
 * Una sola carga: el fichero variable cubre del 200 al 800, así que pedir el 400
 * basta, y el subset `latin` cubre el español.
 */
export const DESCRIPTOR_ATKINSON = `400 16px "${FAMILIA_ATKINSON}"`

/** Lo que se espera como máximo a la fuente antes de montar la app. */
export const TOPE_FUENTE_MS = 1000

const descriptorDe = (familia: string): string =>
  familia === FAMILIA_ATKINSON ? DESCRIPTOR_ATKINSON : `400 16px "${familia}"`

export function esperarFuente(opciones: OpcionesEsperarFuente = {}): Promise<EstadoFuente> {
  const { familia = FAMILIA_ATKINSON, topeMs = TOPE_FUENTE_MS } = opciones
  const fonts: FontFaceSet | undefined = typeof document === 'undefined' ? undefined : document.fonts
  if (!fonts || typeof fonts.load !== 'function') return Promise.resolve('no-soportado')

  return new Promise<EstadoFuente>(resolver => {
    let terminada = false
    const terminar = (estado: EstadoFuente): void => {
      if (terminada) return
      terminada = true
      clearTimeout(temporizador)
      resolver(estado)
    }
    const temporizador = setTimeout(() => terminar('tope'), topeMs)

    try {
      fonts.load(descriptorDe(familia)).then(
        // `load` resuelve con [] si ninguna @font-face coincide: no es «cargada».
        caras => terminar(caras.length > 0 ? 'cargada' : 'error'),
        () => terminar('error'),
      )
    } catch {
      terminar('error')
    }
  })
}
